import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  RefreshCw, Loader2, AlertOctagon, ChevronLeft, ChevronRight,
  ExternalLink, Search, ArrowUp, ArrowDown, ArrowUpDown, FilterX, RotateCcw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DateRangePicker } from '../components/ui/date-range-picker';

// Open the call detail in a new tab, transferring the auth session so the new
// tab isn't bounced to /login. Mirrors the helper used on the Auto Processing
// page and the Calls list.
function openCallInNewTab(callId) {
  const detailPath = `/calls/${encodeURIComponent(callId)}`;
  const newTab = window.open('', '_blank');
  if (!newTab) {
    window.location.href = detailPath;
    return;
  }
  try {
    const token = sessionStorage.getItem('vcm_token');
    const userData = sessionStorage.getItem('vcm_user');
    if (token) newTab.sessionStorage.setItem('vcm_token', token);
    if (userData) newTab.sessionStorage.setItem('vcm_user', userData);
    newTab.location.href = detailPath;
  } catch (err) {
    newTab.location.href = detailPath;
  }
}

function fmtDateTime(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
}

function SortHeader({ label, sortKey, currentSort, order, onSort, defaultOrder = 'desc' }) {
  const active = currentSort === sortKey;
  const Icon = active ? (order === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th className="text-left px-3 py-2 select-none">
      <button
        type="button"
        onClick={() => onSort(sortKey, defaultOrder)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide text-[11px] ${active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
      >
        {label}
        <Icon className="w-3 h-3" />
      </button>
    </th>
  );
}

const EMPTY_FILTERS = {
  fromDate: '',
  toDate: '',
  agent_name: '',
  direction: '',
  error_code: '',
};

export default function FailedCallsTab() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filtersDraft, setFiltersDraft] = useState(EMPTY_FILTERS);
  const [errorCodes, setErrorCodes] = useState([]);
  const [sort, setSort] = useState('last_retry_at');
  const [order, setOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const latestRequestId = useRef(0);
  const limit = 50;

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadData = useCallback(async ({ silent = false } = {}) => {
    const requestId = ++latestRequestId.current;
    if (!silent) setRefreshing(true);
    try {
      const params = {
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        agent_name: filters.agent_name || undefined,
        direction: filters.direction || undefined,
        error_code: filters.error_code || undefined,
        search: searchDebounced || undefined,
        sort,
        order,
        page,
        limit,
      };
      const res = await api.getGeminiPermanentFailedCalls(params);
      if (requestId !== latestRequestId.current) return;
      setRows(res.rows || []);
      setTotal(res.total || 0);
      setPages(res.pages || 1);
      setErrorCodes(res.error_codes || []);
    } catch (err) {
      if (requestId === latestRequestId.current && !silent) {
        toast.error(err?.message || 'Failed to load failed calls');
      }
    } finally {
      if (requestId === latestRequestId.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [filters, searchDebounced, sort, order, page]);

  useEffect(() => { loadData(); }, [loadData]);

  // On-demand "Retry all failed" — kicks off the same sweep as the weekly
  // Sunday cron (runWeeklyFailedRetry) and polls its state until it finishes.
  const [retrying, setRetrying] = useState(false);
  const retryPollRef = useRef(null);
  // Keep the latest loadData reachable from the (stable) poll closure without
  // making the poll depend on it — otherwise a filter change would tear the
  // running poll down and restart it.
  const loadDataRef = useRef(loadData);
  useEffect(() => { loadDataRef.current = loadData; }, [loadData]);

  const stopRetryPoll = useCallback(() => {
    if (retryPollRef.current) {
      clearInterval(retryPollRef.current);
      retryPollRef.current = null;
    }
  }, []);

  const startRetryPoll = useCallback(() => {
    stopRetryPoll();
    retryPollRef.current = setInterval(async () => {
      try {
        const st = await api.getGeminiFailedRetryState();
        if (!st.running) {
          stopRetryPoll();
          setRetrying(false);
          const r = st.lastRun;
          if (r && r.succeeded !== undefined) {
            toast.success(`Retry finished — ${r.succeeded} recovered, ${r.permanently_failed ?? 0} permanent-failed, ${r.deferred ?? 0} deferred (credits/quota)`);
          } else {
            toast.success('Failed-call retry finished');
          }
          loadDataRef.current?.({ silent: true });
        }
      } catch {
        /* transient — keep polling */
      }
    }, 3000);
  }, [stopRetryPoll]);

  // Reflect an already-running sweep (e.g. the weekly cron) on mount, and clean
  // up the interval on unmount.
  useEffect(() => {
    let cancelled = false;
    api.getGeminiFailedRetryState()
      .then((st) => { if (!cancelled && st.running) { setRetrying(true); startRetryPoll(); } })
      .catch(() => {});
    return () => { cancelled = true; stopRetryPoll(); };
  }, [startRetryPoll, stopRetryPoll]);

  const handleRetryAll = async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      await api.retryFailedGeminiNow();
      toast.info('Retrying all failed calls… this runs in the background');
      startRetryPoll();
    } catch (err) {
      const msg = err?.message || '';
      if (/already running/i.test(msg)) {
        toast.info('A failed-call retry is already running');
        startRetryPoll();
      } else {
        setRetrying(false);
        toast.error(msg || 'Could not start retry');
      }
    }
  };

  const handleSort = (key, defaultOrder) => {
    if (sort === key) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setOrder(defaultOrder);
    }
    setPage(1);
  };

  const applyFilters = () => { setFilters(filtersDraft); setPage(1); };
  const clearFilters = () => {
    setFiltersDraft(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setSearch('');
    setPage(1);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <AlertOctagon className="w-5 h-5 text-rose-400" />
        <div className="flex-1 min-w-[200px]">
          <h2 className="text-lg font-semibold">Failed Calls</h2>
          <p className="text-xs text-muted-foreground">
            Calls that failed the Auto Processing pipeline · {total} total
          </p>
        </div>
        <Button
          variant="default"
          size="sm"
          onClick={handleRetryAll}
          disabled={retrying || total === 0}
          title="Retry every failed call now — same sweep as the weekly Sunday job"
          data-testid="failed-calls-retry-all"
        >
          {retrying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}
          {retrying ? 'Retrying…' : 'Retry all failed'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => loadData()}
          disabled={refreshing}
          data-testid="failed-calls-refresh"
        >
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[180px] relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="search call_id / agent / number"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-7 h-8 text-xs"
              data-testid="failed-calls-search"
            />
          </div>
          <div className="text-xs text-muted-foreground">{total} call{total === 1 ? '' : 's'}</div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="agent name"
            value={filtersDraft.agent_name}
            onChange={(e) => setFiltersDraft((d) => ({ ...d, agent_name: e.target.value }))}
            className="h-8 text-xs w-36"
          />
          <select
            value={filtersDraft.direction}
            onChange={(e) => setFiltersDraft((d) => ({ ...d, direction: e.target.value }))}
            className="h-8 text-xs rounded border border-border bg-background px-2"
          >
            <option value="">any direction</option>
            <option value="inbound">inbound</option>
            <option value="outbound">outbound</option>
          </select>
          <select
            value={filtersDraft.error_code}
            onChange={(e) => setFiltersDraft((d) => ({ ...d, error_code: e.target.value }))}
            className="h-8 text-xs rounded border border-border bg-background px-2"
          >
            <option value="">any error code</option>
            {errorCodes.map((code) => (
              <option key={code} value={code}>{code}</option>
            ))}
          </select>
          <DateRangePicker
            value={{
              from_date: filters.fromDate ? filters.fromDate.slice(0, 10) : '',
              to_date: filters.toDate ? filters.toDate.slice(0, 10) : '',
            }}
            showValue={Boolean(filters.fromDate || filters.toDate)}
            placeholder="Date range"
            onChange={(range) => {
              const next = {
                fromDate: range.from_date ? `${range.from_date}T00:00` : '',
                toDate: range.to_date ? `${range.to_date}T23:59` : '',
              };
              setFiltersDraft((d) => ({ ...d, ...next }));
              setFilters((f) => ({ ...f, ...next }));
              setPage(1);
            }}
            onClear={() => {
              setFiltersDraft((d) => ({ ...d, fromDate: '', toDate: '' }));
              setFilters((f) => ({ ...f, fromDate: '', toDate: '' }));
              setPage(1);
            }}
            buttonClassName="h-8 text-xs min-w-[220px]"
          />
          <Button size="sm" variant="secondary" onClick={applyFilters}>Apply</Button>
          <Button size="sm" variant="ghost" onClick={clearFilters}>
            <FilterX className="w-3 h-3 mr-1" />Clear
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Call</th>
                <SortHeader label="Agent"       sortKey="agent_name"   currentSort={sort} order={order} onSort={handleSort} defaultOrder="asc" />
                <SortHeader label="Date/Time"   sortKey="date_time"    currentSort={sort} order={order} onSort={handleSort} />
                <th className="text-left px-3 py-2">Direction</th>
                <SortHeader label="Answered / Total" sortKey="answered" currentSort={sort} order={order} onSort={handleSort} />
                <SortHeader label="Retries"     sortKey="retry_count"  currentSort={sort} order={order} onSort={handleSort} />
                <SortHeader label="Last Retry"  sortKey="last_retry_at" currentSort={sort} order={order} onSort={handleSort} />
                <SortHeader label="Error Code"  sortKey="error_code"   currentSort={sort} order={order} onSort={handleSort} defaultOrder="asc" />
                <th className="text-left px-3 py-2">Failure Reason</th>
                <th className="text-right px-3 py-2">View</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={10} className="text-center text-xs text-muted-foreground p-6">No failed calls</td></tr>
              )}
              {rows.map((c) => {
                const reason = c.failure_reason || c.gemini_error || '-';
                return (
                  <tr key={c.call_id} className="border-t border-border hover:bg-white/[0.02]">
                    <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <span>{c.call_id}</span>
                        {c.permanent_failed && (
                          <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 font-semibold" title="Failed again on the weekly retry">
                            Permanent
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">{c.agent_name || '-'}</td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{c.date} {c.time}</td>
                    <td className="px-3 py-2">
                      {c.direction
                        ? <span className={`text-xs px-2 py-0.5 rounded ${c.direction === 'inbound' ? 'bg-sky-500/15 text-sky-300' : 'bg-teal-500/15 text-teal-300'}`}>{c.direction}</span>
                        : <span className="text-xs text-muted-foreground">-</span>}
                    </td>
                    <td
                      className="px-3 py-2 text-xs tabular-nums whitespace-nowrap"
                      title={`call_duration=${c.call_duration}s · answered=${c.answered_seconds ?? 0}s`}
                    >
                      <span>{c.answered_seconds ?? 0}s</span>
                      <span className="text-muted-foreground"> / {c.call_duration}s</span>
                    </td>
                    <td className="px-3 py-2 text-xs tabular-nums">
                      <span className="inline-block px-2 py-0.5 rounded bg-rose-500/15 text-rose-300 font-medium">{c.retry_count ?? 0}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(c.last_retry_at)}</td>
                    <td className="px-3 py-2">
                      {c.error_code
                        ? <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-amber-500/15 text-amber-300">{c.error_code}</span>
                        : <span className="text-xs text-muted-foreground">-</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground max-w-[280px] truncate" title={reason}>{reason}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => openCallInNewTab(c.call_id)}
                        className="text-primary hover:underline inline-flex items-center gap-1 text-xs"
                        data-testid="failed-calls-view-details"
                      >
                        View Details <ExternalLink className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-3 py-2">
            <span className="text-xs text-muted-foreground">Page {page} of {pages}</span>
            <div className="flex items-center gap-1">
              <Button variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
